import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import type { Fan, TokensObject } from 'Services/IdpService';
import IdpClient, { IdpFanCenterRequestType, IdpRequestType, UserEvents } from 'Services/IdpService';

interface IdpContextType {
  idpInited: boolean;
  user: Fan | null;
  userLogged: boolean;
  accessToken: string | null;
  loading: boolean;
  isReady: boolean;
  showLogin: () => Promise<void>;
  showProfile: () => Promise<void>;
  showNewsletter: () => Promise<void>;
  logout: () => void;
  getIsSubscribed: (newsletterId: string) => boolean;
  getTokens: () => Promise<TokensObject>;
  refreshUserData: () => Promise<void>;
}

const IdpContext = createContext<IdpContextType | undefined>(undefined);

interface IdpProviderProps {
  children: ReactNode;
}

// Provider Component
export const IdpProvider = ({ children }: IdpProviderProps) => {
  const [idpInited, setIdpInited] = useState(false);
  const [user, setUser] = useState<Fan | null>(null);
  const [userLogged, setUserLogged] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);

  const initialSetupDone = useRef(false);
  const eventListenersSetup = useRef(false);

  // Step 1: Initialize IDP
  useEffect(() => {
    const initIdp = async () => {
      try {
        console.log('Initializing IDP...');
        if (!window.IdpManager) {
          await IdpClient.init('en');
        }
        console.log('IDP initialized');
        setIdpInited(true);
      } catch (error) {
        console.error('Failed to initialize IDP:', error);
        setLoading(false);
        setIsReady(true);
      }
    };

    if (!initialSetupDone.current) {
      initIdp();
      initialSetupDone.current = true;
    }
  }, []);

  /* Private event handlers */
  const handleLogin = useCallback(
    async (userData?: Fan) => {
      console.log(`User logged in: ${userData?.given_name || 'Unknown'}`);

      setUser(userData || null);
      setUserLogged(true);

      // Get fresh access token
      try {
        if (window.IdpManager) {
          const token = await window.IdpManager.getAccessToken();
          setAccessToken(token);
        }
      } catch (error) {
        console.error('Failed to get access token after login:', error);
      }

      if (!isReady) {
        setLoading(false);
        setIsReady(true);
      }
    },
    [isReady],
  );

  const handleLogout = useCallback(() => {
    console.log('User logged out');
    setUser(null);
    setUserLogged(false);
    setAccessToken(null);
  }, []);

  const handleUserUpdate = useCallback(
    (userData?: Fan) => {
      console.log(`User data updated: ${userData?.given_name || 'Unknown'}`);
      setUser(userData || null);

      if (userData && !userLogged) {
        setUserLogged(true);
      }
    },
    [userLogged],
  );

  // Check initial authentication status
  const checkInitialAuthStatus = useCallback(async () => {
    if (!window.IdpManager?.isReady) return;

    try {
      console.log('Checking initial auth status...');

      const isLoggedIn = await window.IdpManager.isFanLoggedIn();
      console.log('User logged in:', isLoggedIn);

      if (isLoggedIn) {
        const token = await window.IdpManager.getAccessToken();
        setAccessToken(token);
        setUserLogged(true);

        setTimeout(() => {
          if (!isReady) {
            console.log('Timeout reached, marking as ready');
            setLoading(false);
            setIsReady(true);
          }
        }, 3000);
      } else {
        setUserLogged(false);
        setUser(null);
        setAccessToken(null);
        setLoading(false);
        setIsReady(true);
        console.log('User not logged in, marked as ready');
      }
    } catch (error) {
      console.error('Failed to check initial auth status:', error);
      setLoading(false);
      setIsReady(true);
    }
  }, [isReady]);

  // Step 2: Setup event listeners and check auth when IDP is ready
  useEffect(() => {
    if (!idpInited || eventListenersSetup.current) return;

    const setupEventsAndAuth = async () => {
      let attempts = 0;
      const maxAttempts = 50;

      while (!window.IdpManager?.isReady && attempts < maxAttempts) {
        await new Promise((resolve) => setTimeout(resolve, 100));
        attempts++;
      }

      if (!window.IdpManager?.isReady) {
        console.error('IdpManager not ready after waiting');
        setLoading(false);
        setIsReady(true);
        return;
      }

      console.log('Setting up IDP events...');
      const eventEmitter = window.IdpManager.getEventEmitter();

      eventEmitter.on(UserEvents.LOGIN, handleLogin);
      eventEmitter.on(UserEvents.LOGIN_SILENT, handleLogin);
      eventEmitter.on(UserEvents.LOGOUT, handleLogout);
      eventEmitter.on(UserEvents.FAN_LOADED, handleUserUpdate);
      eventEmitter.on(UserEvents.FAN_UPDATED, handleUserUpdate);

      eventListenersSetup.current = true;

      await checkInitialAuthStatus();
    };

    setupEventsAndAuth();

    return () => {
      if (window.IdpManager?.isReady && eventListenersSetup.current) {
        const eventEmitter = window.IdpManager.getEventEmitter();
        eventEmitter.off(UserEvents.LOGIN, handleLogin);
        eventEmitter.off(UserEvents.LOGIN_SILENT, handleLogin);
        eventEmitter.off(UserEvents.LOGOUT, handleLogout);
        eventEmitter.off(UserEvents.FAN_LOADED, handleUserUpdate);
        eventEmitter.off(UserEvents.FAN_UPDATED, handleUserUpdate);
        eventListenersSetup.current = false;
      }
    };
  }, [idpInited, handleLogin, handleLogout, handleUserUpdate, checkInitialAuthStatus]);

  /** Public functions */
  const showProfile = useCallback(async () => {
    if (!isReady || !window.IdpManager) return;

    try {
      await window.IdpManager.showWebProfile({
        requestType: IdpFanCenterRequestType.Redirect,
      });
    } catch (error) {
      console.error('Failed to show profile:', error);
    }
  }, [isReady]);

  const showLogin = useCallback(async () => {
    if (!isReady || !window.IdpManager) return;

    try {
      await window.IdpManager.login({
        requestType: IdpRequestType.Redirect,
        locale: 'en',
      });
    } catch (error) {
      console.error('Failed to show login:', error);
    }
  }, [isReady]);

  const showNewsletter = useCallback(async () => {
    if (!isReady || !window.IdpManager) return;

    try {
      await window.IdpManager.showWebProfile({
        requestType: IdpFanCenterRequestType.Redirect,
      });
    } catch (error) {
      console.error('Failed to show newsletter:', error);
    }
  }, [isReady]);

  const getIsSubscribed = useCallback((newsletterId: string) => {
    return newsletterId === '@';
  }, []);

  const getTokens = useCallback(async () => {
    if (!isReady || !window.IdpManager) return null;

    try {
      return await window.IdpManager.getTokens();
    } catch (error) {
      console.error('Failed to get tokens:', error);
      return null;
    }
  }, [isReady]);

  const logout = useCallback(() => {
    if (!isReady || !window.IdpManager) return;
    handleLogout();
  }, [handleLogout, isReady]);

  const refreshUserData = useCallback(async () => {
    if (!isReady) return;
    await checkInitialAuthStatus();
  }, [isReady, checkInitialAuthStatus]);

  const contextValue: IdpContextType = useMemo(
    () => ({
      idpInited,
      user,
      userLogged,
      accessToken,
      loading,
      isReady,
      showLogin,
      showProfile,
      showNewsletter,
      logout,
      getIsSubscribed,
      getTokens,
      refreshUserData,
    }),
    [
      idpInited,
      user,
      userLogged,
      accessToken,
      loading,
      isReady,
      showLogin,
      showProfile,
      showNewsletter,
      logout,
      getIsSubscribed,
      getTokens,
      refreshUserData,
    ],
  );

  return <IdpContext.Provider value={contextValue}>{children}</IdpContext.Provider>;
};

export const useIdp = (): IdpContextType => {
  const context = useContext(IdpContext);

  if (context === undefined) {
    throw new Error('useIdp must be used within an IdpProvider');
  }

  return context;
};

export default useIdp;
